import { WorkspaceRole, WORKSPACE_ROLE_RANK } from '../enums/auth.enum';
import { Feature, Op } from './feature';

const F = Feature;
type OpMap = Record<Op, Feature[]>;

/**
 * Features EVERY workspace role may use, per op, AND that skip the live membership lookup in
 * `RoleAccessGuard`. Only SELF-SERVICE routes belong here (session, profile, notifications,
 * preferences, onboarding): a removed member with a stale token must still be able to log out
 * or switch workspace. Anything that reads workspace/org data is a VIEWER-floor feature instead,
 * so the membership lookup (and the `NoLongerMember` 403) applies.
 */
export const COMMON: OpMap = {
  get: [
    F.AUTH_SESSION, F.NOTIFICATIONS, F.ONBOARDING, F.PREFERENCES, F.PROFILE,
  ],
  post: [F.AUTH_SESSION, F.NOTIFICATIONS, F.ONBOARDING, F.PROFILE],
  put: [F.NOTIFICATIONS, F.PREFERENCES],
  patch: [F.NOTIFICATIONS, F.PROFILE],
  delete: [F.AUTH_SESSION],
};

/**
 * Cumulative by construction: each role spreads the one below it and adds only its own
 * features, so a list can never drift into "admin can't do what editor can". The floor
 * semantics match `WorkspaceRoleGuard` (a rank floor, not a set).
 *
 * These lists encode the DECIDED target floors (snapshot `floor`), which differ from
 * today's guards on the routes carrying a `decision` in the snapshot.
 */
const VIEWER: OpMap = {
  get: [
    ...COMMON.get, F.BILLING, F.DWD_WORKSPACE, F.EMAIL_ACCOUNTS, F.ORG_WORKSPACES_LIST, F.RATE_LIMIT, F.REPORTS,
    F.REVIEW_REWARDS, F.TAGS, F.TEAM, F.TEMPLATE, F.WORKSPACE_DETAILS, F.WORKSPACES,
  ],
  post: [...COMMON.post, F.REVIEW_REWARDS],
  put: [...COMMON.put],
  patch: [...COMMON.patch],
  delete: [...COMMON.delete],
};

const EDITOR: OpMap = {
  get: [...VIEWER.get, F.API_KEYS, F.EMAIL_ACCOUNT_CONNECTION],
  post: [...VIEWER.post, F.API_KEYS, F.EMAIL_ACCOUNTS, F.TAGS],
  put: [...VIEWER.put, F.TAGS],
  patch: [...VIEWER.patch, F.EMAIL_ACCOUNTS],
  delete: [...VIEWER.delete, F.API_KEYS, F.EMAIL_ACCOUNTS],
};

const ADMIN: OpMap = {
  get: [
    ...EDITOR.get, F.AI_CONTENT, F.AUDIT, F.BILLING_API, F.DWD_WORKSPACE_ADMIN, F.ORGANIZATION,
    F.TEAM_INVITE_LINKS, F.TEAM_INVITES,
  ],
  post: [
    ...EDITOR.post, F.DWD_WORKSPACE_ADMIN, F.EMAIL_ACCOUNTS_BULK_CONNECT, F.TEAM, F.TEAM_INVITE_LINKS, F.TEMPLATE,
  ],
  put: [...EDITOR.put, F.TEMPLATE],
  patch: [...EDITOR.patch, F.TEAM, F.TEMPLATE],
  delete: [...EDITOR.delete, F.DWD_WORKSPACE_ADMIN, F.TEAM, F.TEAM_INVITE_LINKS, F.TEMPLATE],
};

const OWNER: OpMap = {
  get: [
    ...ADMIN.get, F.BILLING_UPGRADE_PREVIEW, F.ORG_WORKSPACES, F.ORGANIZATION_MEMBERS, F.ORGANIZATION_USAGE,
    F.PARTNER, F.SALESHANDY, F.WORKSPACE_QUOTA,
  ],
  post: [
    ...ADMIN.post, F.BILLING, F.ORG_WORKSPACES, F.ORGANIZATION_MEMBERS, F.PARTNER, F.SALESHANDY, F.WORKSPACES,
  ],
  put: [...ADMIN.put],
  patch: [
    ...ADMIN.patch, F.BILLING, F.ORG_WORKSPACES, F.ORGANIZATION, F.ORGANIZATION_MEMBERS, F.WORKSPACE_DETAILS,
    F.WORKSPACE_QUOTA, F.WORKSPACES,
  ],
  delete: [...ADMIN.delete, F.ORGANIZATION_MEMBERS, F.PARTNER, F.WORKSPACES],
};

export const ROLE_ACCESS: Record<WorkspaceRole, OpMap> = {
  [WorkspaceRole.VIEWER]: VIEWER,
  [WorkspaceRole.EDITOR]: EDITOR,
  [WorkspaceRole.ADMIN]: ADMIN,
  [WorkspaceRole.OWNER]: OWNER,
};

/** May `role` perform `op` on `feature`? Unknown role or feature denies. */
export function canRoleDo(role: WorkspaceRole, feature: Feature, op: Op): boolean {
  if (!(role in WORKSPACE_ROLE_RANK)) return false;
  return ROLE_ACCESS[role]?.[op]?.includes(feature) ?? false;
}
